import { useQuery } from '@tanstack/react-query';
import { budgetsApi } from '../api';
import type { BudgetListParams } from '../api';

export const budgetQueryKeys = {
  all: ['budgets'] as const,
  years: () => [...budgetQueryKeys.all, 'years'] as const,
  /** Same list as SearchableSelect + `getBudgetYears` on reports (Risk Triage, School Profile, etc.). */
  reportYearsList: () => [...budgetQueryKeys.all, 'reportYearsList'] as const,
  governorates: () => [...budgetQueryKeys.all, 'governorates'] as const,
  cities: (governorateId: string | undefined) =>
    [...budgetQueryKeys.all, 'cities', governorateId ?? ''] as const,
  summary: (params: string) => [...budgetQueryKeys.all, 'summary', params] as const,
  budgetOverviewMainDashboard: (params: string) =>
    [...budgetQueryKeys.all, 'budgetOverviewMainDashboard', params] as const,
  byGovernorate: (params: string) =>
    [...budgetQueryKeys.all, 'byGovernorate', params] as const,
  byDay: (params: string) => [...budgetQueryKeys.all, 'byDay', params] as const,
  list: (params: string) => [...budgetQueryKeys.all, 'list', params] as const,
  withFilters: (params: string) => [...budgetQueryKeys.all, 'withFilters', params] as const,
  tuitionStatistics: (params: string) => [...budgetQueryKeys.all, 'tuitionStatistics', params] as const,
};

export function useBudgetYearsQuery() {
  return useQuery({
    queryKey: budgetQueryKeys.years(),
    queryFn: () => budgetsApi.getYears(),
  });
}

export function useBudgetReportYearsListQuery() {
  return useQuery<number[]>({
    queryKey: budgetQueryKeys.reportYearsList(),
    queryFn: async () => (await budgetsApi.getBudgetYears({ skipCount: 0, maxResultCount: 100 })) ?? [],
    staleTime: 5 * 60 * 1000,
  });
}

export function useBudgetGovernoratesQuery() {
  return useQuery({
    queryKey: budgetQueryKeys.governorates(),
    queryFn: () => budgetsApi.getGovernorates(),
  });
}

export function useBudgetCitiesQuery(governorateId: string | undefined) {
  return useQuery({
    queryKey: budgetQueryKeys.cities(governorateId),
    queryFn: () => budgetsApi.getCities(governorateId),
  });
}

export function useBudgetSummaryQuery(params: {
  year?: string;
  governorateId?: string;
  cityId?: string;
}) {
  const paramStr = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    )
  ).toString();
  return useQuery({
    queryKey: budgetQueryKeys.summary(paramStr),
    queryFn: () => budgetsApi.getSummary(params),
  });
}

export function useBudgetOverviewMainDashboardQuery(params: {
  year: string;
  governorateId?: string;
}) {
  const year = params.year?.trim() || '';
  const governorateId = params.governorateId?.trim() || undefined;
  const paramStr = [year && `year=${year}`, governorateId && `governorateId=${governorateId}`]
    .filter(Boolean)
    .join('&');
  return useQuery({
    queryKey: budgetQueryKeys.budgetOverviewMainDashboard(paramStr),
    queryFn: () =>
      budgetsApi.getBudgetOverviewMainDashboard({
        year: parseInt(year, 10),
        governorateId: governorateId ? parseInt(governorateId, 10) : undefined,
      }),
    enabled: !!year,
  });
}

export function useBudgetByGovernorateQuery(params: {
  year?: string;
  governorateId?: string;
  cityId?: string;
}) {
  const paramStr = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    )
  ).toString();
  return useQuery({
    queryKey: budgetQueryKeys.byGovernorate(paramStr),
    queryFn: () => budgetsApi.getByGovernorate(params),
  });
}

export function useBudgetByDayQuery(params: {
  year?: string;
  governorateId?: string;
  cityId?: string;
}) {
  const paramStr = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    )
  ).toString();
  return useQuery({
    queryKey: budgetQueryKeys.byDay(paramStr),
    queryFn: () => budgetsApi.getByDay(params),
  });
}

export function useTuitionStatisticsQuery(params: {
  year: string;
  governorateId?: string;
}) {
  const year = params.year?.trim() || '';
  const governorateId = params.governorateId?.trim() || undefined;
  const paramStr = [year && `year=${year}`, governorateId && `governorateId=${governorateId}`]
    .filter(Boolean)
    .join('&');
  return useQuery({
    queryKey: budgetQueryKeys.tuitionStatistics(paramStr),
    queryFn: () =>
      budgetsApi.getTuitionStatistics({
        year: parseInt(year, 10),
        governorateId: governorateId ? parseInt(governorateId, 10) : undefined,
      }),
    enabled: !!year && !Number.isNaN(parseInt(year, 10)),
  });
}

export function useBudgetListQuery(params: BudgetListParams) {
  const paramStr = new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== '' && v !== null
      )
    ) as Record<string, string>
  ).toString();
  return useQuery({
    queryKey: budgetQueryKeys.withFilters(paramStr),
    queryFn: async () => {
      const apiParams = budgetsApi.listParamsToWithFilters(params);
      const res = await budgetsApi.getWithFilters(apiParams);
      return res;
    },
  });
}
